import { defaultItemSorter, defaultPositionKey } from './defaults'
import { arrayManager, identifierFor } from './utils'

export const idsMatch = (map, item1, item2) =>
  identifierFor(item1, map) === identifierFor(item2, map)

/**
 * Calculates and returns the updated position for the provided item.
 *
 * Note that "position" in this case is not referring to its index in the list,
 * but rather the very large value used by ArrayManager.
 */
function getNewPosition({ amount, idMap, item, items }) {
  const curIdx = items.findIndex(idsMatch.bind(this, idMap, item))
  const newIdx = curIdx + amount
  if (newIdx < 0 || newIdx > items.length) {
    return item.position
  }

  const withoutItems = items.slice()
  withoutItems.splice(curIdx, 1)
  return arrayManager.insertAt(withoutItems, newIdx)[newIdx]
}

/**
 * Finds the new position for the item and submits a request to update the item accordingly.
 */
export function updateItemPosition({ item, updateItem, ...itemDetails }) {
  const newPosition = getNewPosition({ item, ...itemDetails })
  if (updateItem && newPosition !== item.position) {
    updateItem({ position: newPosition })
  }
  return newPosition
}

/**
 * Returns a copy of the provided item with its "position" property updated accordingly.
 */
const movedItem = ({ item, ...itemDetails }) => ({
  ...item,
  position:
    updateItemPosition({
      item,
      ...itemDetails,
    }) || item.position,
})

/**
 * Builds a re-ordered "items" list with "item" moved by "amount".
 */
export const moveByReducer =
  ({
    amount,
    item,
    itemSorter = defaultItemSorter,
    positionKey = defaultPositionKey,
    updateItem,
  }) =>
  ({ idMap, items }) => ({
    items: items
      .map((thisItem) =>
        idsMatch(idMap, item, thisItem)
          ? movedItem({
              amount,
              idMap,
              item,
              items,
              updateItem,
            })
          : thisItem
      )
      .sort(itemSorter(positionKey)),
  })
